import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput, { formatINR } from '../../components/SliderInput';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

// Indian Income Tax Slabs (FY 2024-25)
const NEW_REGIME = [
  { upto: 300000, rate: 0 },
  { upto: 600000, rate: 5 },
  { upto: 900000, rate: 10 },
  { upto: 1200000, rate: 15 },
  { upto: 1500000, rate: 20 },
  { upto: Infinity, rate: 30 },
];
const OLD_REGIME = [
  { upto: 250000, rate: 0 },
  { upto: 500000, rate: 5 },
  { upto: 1000000, rate: 20 },
  { upto: Infinity, rate: 30 },
];

const computeTax = (taxable, slabs) => {
  let tax = 0;
  let remaining = taxable;
  let prev = 0;
  for (const s of slabs) {
    if (remaining <= 0) break;
    const slabAmount = Math.min(remaining, s.upto - prev);
    tax += (slabAmount * s.rate) / 100;
    remaining -= slabAmount;
    prev = s.upto;
  }
  return tax;
};

const IncomeTaxCalculator = () => {
  const [income, setIncome] = useState(1200000);
  const [deductions, setDeductions] = useState(150000);
  const [regime, setRegime] = useState('new');

  const result = useMemo(() => {
    const slabs = regime === 'new' ? NEW_REGIME : OLD_REGIME;
    // New regime gets standard deduction ₹75,000 from FY 2024-25; Old gives ₹50,000 + Section 80C deductions
    const standardDed = regime === 'new' ? 75000 : 50000;
    const eligibleDeductions = regime === 'new' ? standardDed : Math.min(deductions, 150000) + standardDed;
    const taxable = Math.max(income - eligibleDeductions, 0);
    let tax = computeTax(taxable, slabs);
    // Rebate u/s 87A
    const rebateLimit = regime === 'new' ? 700000 : 500000;
    const rebateAmount = regime === 'new' ? 25000 : 12500;
    if (taxable <= rebateLimit) tax = Math.max(tax - rebateAmount, 0);
    const cess = tax * 0.04; // 4% Health & Education Cess
    const total = tax + cess;
    return { taxable, tax, cess, total, take: income - total, eligibleDeductions };
  }, [income, deductions, regime]);

  return (
    <CalculatorLayout
      title="Income Tax Calculator"
      subtitle="Estimate your Indian income tax for FY 2024-25 under new and old tax regimes."
      category="Financial"
      description={<>
        <p>This calculator estimates your income tax based on the latest Indian tax slabs for FY 2024-25 (AY 2025-26). It applies the standard deduction, Section 80C limit (old regime), Section 87A rebate, and 4% Health & Education Cess.</p>
        <p><strong>New Regime</strong>: Lower slabs, ₹75,000 standard deduction, rebate up to ₹7L taxable income. <strong>Old Regime</strong>: Higher slabs but allows Section 80C and other deductions.</p>
      </>}
      formulaNote="Final Tax = Slab Tax − 87A Rebate + 4% Cess"
    >
      <Tabs value={regime} onValueChange={setRegime} className="mb-5">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="new">New Regime (Default)</TabsTrigger>
          <TabsTrigger value="old">Old Regime</TabsTrigger>
        </TabsList>
        <TabsContent value="new" />
        <TabsContent value="old" />
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <SliderInput label="Annual Gross Income" unit="₹" unitColor="saffron" value={income} onChange={setIncome} min={100000} max={50000000} step={10000} format={formatINR} />
          {regime === 'old' && (
            <SliderInput label="Deductions (80C, etc.)" unit="₹" unitColor="green" value={deductions} onChange={setDeductions} min={0} max={300000} step={1000} format={formatINR} hint="Max ₹1.5L under Section 80C" />
          )}
          {regime === 'new' && (
            <div className="p-4 bg-indgreen-50 border border-indgreen-200 rounded-lg text-sm text-slate-700">
              <strong>New Regime auto-applies:</strong> ₹75,000 standard deduction. No 80C deductions allowed.
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Tax Payable</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-saffron-700 display-font mt-1">{formatINR(result.total)}</div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <Box label="Eligible Deductions" value={formatINR(result.eligibleDeductions)} />
            <Box label="Taxable Income" value={formatINR(result.taxable)} />
            <Box label="Slab Tax" value={formatINR(result.tax)} />
            <Box label="Health & Edu Cess (4%)" value={formatINR(result.cess)} />
            <div className="col-span-2">
              <Box label="Take-Home (after tax)" value={formatINR(result.take)} tone="green" big />
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Box = ({ label, value, tone, big }) => {
  const c = tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-3 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c} ${big ? 'text-lg' : ''}`}>{value}</div>
    </div>
  );
};

export default IncomeTaxCalculator;
